import http from 'hub-http/clients/apiClient';
import { createRpcClientV2 } from 'rpc-client-utils';
import { useQuery, registerQuery } from 'data-fetching-client';
import { useEffect, useMemo, useCallback, useState } from 'react';
import { View, ViewsSearchQueryType } from '../types/ViewTypes';
import { transformViewWithPermissionsToInternalFormat } from '../dataOperators/composableSearchViews';
import Raven from 'raven-js';
import { usePagingState } from './usePagingState';
import { sanitizeQuery } from '../utils/sanitizeQuery';
import { useObjectTypeIdsInNamespaces } from '../../objectDefinitions/hooks/useObjectTypeIdsInNamespaces';
import { useHasBulkActionsGate } from '../../userInfo/hooks/useHasBulkActionsGate';
import { getViewsByComposableSearchRpc } from '../../../__generated__/chirp/com/hubspot/sales/rpc/ViewsCrudRpc';
import {
  buildComposableSearchRequest,
  COMPOSABLE_SEARCH_TIMEOUT_MS,
  DEFAULT_PAGE_SIZE,
} from '../utils/composableSearchRequest';

// Re-exported so existing importers keep working; both now live in a leaf
// module that the early requester can also import. See that file for why.
export { DEFAULT_PAGE_SIZE, COMPOSABLE_SEARCH_TIMEOUT_MS };
import type { GetViewsByComposableSearchResponseType } from '../../../__generated__/chirp/com/hubspot/sales/rpc/ViewsCrudRpc';
export type ProcessedViewsSearchResponseType = {
  total: number;
  results: Array<View>;
  paging?: {
    next: {
      after: string;
    };
  };
};

export const VIEWS_SEARCH_QUERY_NAME = 'viewsSearch' as const;

const chirpClient = createRpcClientV2({ hubHttpClient: http });

const fetchComposableSearch = (
  query: ViewsSearchQueryType
): Promise<GetViewsByComposableSearchResponseType> =>
  chirpClient.call(getViewsByComposableSearchRpc, {
    timeout: COMPOSABLE_SEARCH_TIMEOUT_MS,
    request: buildComposableSearchRequest(query),
  });

export const transformComposableSearchResponse = (
  response: GetViewsByComposableSearchResponseType
): ProcessedViewsSearchResponseType => ({
  total: response.total,
  results: (response.results || []).map(
    transformViewWithPermissionsToInternalFormat
  ),
  // CHIRP paginates with {hasMore, offset}; adapt to the {paging:{next:{after}}}
  // contract that usePagingState already understands.
  paging:
    response.hasMore && response.offset
      ? { next: { after: response.offset } }
      : undefined,
});

export const ViewsSearchQuery = registerQuery<
  typeof VIEWS_SEARCH_QUERY_NAME,
  ViewsSearchQueryType,
  ProcessedViewsSearchResponseType
>({
  fieldName: VIEWS_SEARCH_QUERY_NAME,
  args: [
    'viewName',
    'ownerIds',
    'teamIds',
    'objectTypeIds',
    'private',
    'limit',
    'pagingToken',
    'sortColumn',
    'sortOrder',
    'namespace',
    'includeLastUsed',
    'lastUsedStart',
    'lastUsedEnd',
  ],
  fetcher: (variables) =>
    fetchComposableSearch(variables).then(transformComposableSearchResponse),
});

export const useFetchViewsSearch = (
  externalSearchQueryObject: ViewsSearchQueryType
) => {
  const {
    currentPagingToken,
    addPagingToken,
    resetPaginationState,
    goToPreviousPage: paginationGoToPreviousPage,
    goToNextPage: paginationGoToNextPage,
    hasNextPage,
    hasPreviousPage,
    previousPagingToken,
    nextPagingToken,
  } = usePagingState();

  const [internalQueryObject, setQueryObject] =
    useState<ViewsSearchQueryType | null>(null);

  // Optimistic removals: after a delete we drop the affected rows locally and
  // shave the total instead of refetching the current page. The search index
  // lags a delete by ~1s, so refetching the same keyset cursor returns the
  // just-deleted ids (and never backfills to a full page) -- an optimistic
  // patch keeps the table and the count correct immediately. The removals are
  // stamped with the response they apply to (`appliesTo`), so as soon as a
  // fresh server response arrives (paging, refetch, filter/sort change) they
  // stop applying and never mask real data -- no reset effect needed.
  const [optimisticRemoval, setOptimisticRemoval] = useState<{
    // Matches the shape of `useQuery`'s `data` (keyed by the query field name),
    // so identity comparisons against `queryResult.data` type-check.
    appliesTo:
      | Record<typeof VIEWS_SEARCH_QUERY_NAME, ProcessedViewsSearchResponseType>
      | undefined;
    ids: Set<string>;
  }>({ appliesTo: undefined, ids: new Set<string>() });

  const namespaces = useMemo(
    () => [externalSearchQueryObject.namespace],
    [externalSearchQueryObject.namespace]
  );

  const { data: defaultObjectTypeIds, loading: defaultObjectTypeIdsLoading } =
    useObjectTypeIdsInNamespaces({ namespaces });

  const sanitizedQuery = useMemo(
    () => sanitizeQuery(externalSearchQueryObject, defaultObjectTypeIds || []),
    [externalSearchQueryObject, defaultObjectTypeIds]
  );

  const includeLastUsed = useHasBulkActionsGate();

  const queryResult = useQuery(ViewsSearchQuery, {
    skip: internalQueryObject === null,
    variables: {
      ...internalQueryObject,
      pagingToken: currentPagingToken,
      includeLastUsed,
    } as ViewsSearchQueryType,
    onError: (e) => {
      Raven.captureException(e, {
        extra: {
          sanitizedQuery,
          internalQueryObject,
          currentPagingToken,
        },
      });
    },
    // notifyOnNetworkStatusChange: true, //this will update queryResult.loading whenever we page through
  });

  useEffect(() => {
    if (defaultObjectTypeIdsLoading) {
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setQueryObject(sanitizedQuery);
    resetPaginationState();
  }, [defaultObjectTypeIdsLoading, sanitizedQuery, resetPaginationState]);

  /**
   * This useEffect looks through the new queryResult and determines if
   * - did the user go to the next page and unconver a new paging token
   *   If so, it appends the new paging token to the array
   * - did the user go to the next page and realize they are at the last page
   *   If so, it appends END_OF_PAGING to the paging tokens array
   **/
  useEffect(() => {
    if (!queryResult.data) {
      return;
    }
    const newPagingToken =
      queryResult.data.viewsSearch.paging &&
      queryResult.data.viewsSearch.paging.next.after;

    addPagingToken(newPagingToken);
  }, [addPagingToken, queryResult.data]);

  const removeViewsOptimistically = useCallback(
    (ids: string[]) => {
      setOptimisticRemoval((prev) => {
        // Keep accumulating only while still on the same response; otherwise
        // start fresh against the current one.
        const base =
          prev.appliesTo === queryResult.data ? prev.ids : new Set<string>();
        const nextIds = new Set(base);
        ids.forEach((id) => nextIds.add(id));
        return { appliesTo: queryResult.data, ids: nextIds };
      });
    },
    [queryResult.data]
  );

  const goToPreviousPage = useCallback(() => {
    if (hasPreviousPage) {
      paginationGoToPreviousPage();
      setQueryObject({
        ...sanitizedQuery,
        pagingToken: previousPagingToken,
      });
    }
  }, [
    sanitizedQuery,
    hasPreviousPage,
    paginationGoToPreviousPage,
    previousPagingToken,
  ]);

  const goToNextPage = useCallback(() => {
    if (hasNextPage) {
      paginationGoToNextPage();
      setQueryObject({
        ...sanitizedQuery,
        pagingToken: nextPagingToken,
      });
    }
  }, [hasNextPage, sanitizedQuery, paginationGoToNextPage, nextPagingToken]);

  // Apply optimistic removals to what callers see: drop the deleted rows and
  // shave the total. Internal effects above still read the raw `queryResult`,
  // so paging tokens are unaffected.
  const visibleData = useMemo(() => {
    const raw = queryResult.data;
    // Only apply removals that were stamped against this exact response; a
    // newer response clears them implicitly.
    if (
      !raw ||
      optimisticRemoval.appliesTo !== raw ||
      optimisticRemoval.ids.size === 0
    ) {
      return raw;
    }
    return {
      ...raw,
      viewsSearch: {
        ...raw.viewsSearch,
        results: raw.viewsSearch.results.filter(
          (view) => !optimisticRemoval.ids.has(String(view.id))
        ),
        total: Math.max(0, raw.viewsSearch.total - optimisticRemoval.ids.size),
      },
    };
  }, [queryResult.data, optimisticRemoval]);

  return useMemo(
    () => ({
      ...queryResult,
      data: visibleData,
      removeViewsOptimistically,
      goToNextPage,
      goToPreviousPage,
      hasNextPage,
      hasPreviousPage,
    }),
    [
      queryResult,
      visibleData,
      removeViewsOptimistically,
      goToNextPage,
      hasNextPage,
      goToPreviousPage,
      hasPreviousPage,
    ]
  );
};

export type FetchViewsSearchErrorType = ReturnType<
  typeof useFetchViewsSearch
>['error'];

export type FetchViewsSearchLoadingType = ReturnType<
  typeof useFetchViewsSearch
>['loading'];

export type FetchViewsSearchDataType = ReturnType<
  typeof useFetchViewsSearch
>['data'];
